import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { caseAPI, entityAPI, alertAPI, aiAPI } from '../lib/api';
import { formatDate, formatRelativeTime, getRiskClass } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Plus,
  Eye,
  Filter,
  RefreshCw,
  FileText,
  FolderOpen,
  Clock,
  CheckCircle,
  Archive,
  ArrowLeft,
  MessageSquare,
  Users,
  AlertTriangle,
  Send,
  Sparkles,
  Download,
  Edit,
  Save,
  X,
  Trash2,
  CheckSquare,
  Square,
  UserPlus,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const CASE_STATUSES = [
  { value: 'open', label: 'Open', icon: FolderOpen, color: 'text-[#00F0FF] border-[#00F0FF] bg-[#00F0FF]/10' },
  { value: 'in_progress', label: 'In Progress', icon: Clock, color: 'text-[#FF9900] border-[#FF9900] bg-[#FF9900]/10' },
  { value: 'pending_review', label: 'Pending Review', icon: FileText, color: 'text-[#A855F7] border-[#A855F7] bg-[#A855F7]/10' },
  { value: 'closed', label: 'Closed', icon: CheckCircle, color: 'text-[#00FF94] border-[#00FF94] bg-[#00FF94]/10' },
  { value: 'archived', label: 'Archived', icon: Archive, color: 'text-muted-foreground border-muted-foreground bg-muted/10' },
];

const PRIORITIES = ['critical', 'high', 'medium', 'low'];

// Case List Component
function CaseList({ onSelectCase }) {
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [page, setPage] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCases, setSelectedCases] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState({ status: '', priority: '', assigned_to: '' });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [newCase, setNewCase] = useState({
    title: '',
    description: '',
    priority: 'medium',
  });

  const limit = 20;

  useEffect(() => {
    fetchCases();
  }, [page, status, priority]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        ...(status && { status }),
        ...(priority && { priority }),
      };
      const response = await caseAPI.list(params);
      setCases(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      const response = await caseAPI.create(newCase);
      toast.success('Case created successfully');
      setShowCreateModal(false);
      setNewCase({ title: '', description: '', priority: 'medium' });
      fetchCases();
      // Navigate to the new case
      if (response.data?.id) {
        onSelectCase(response.data.id);
      }
    } catch (error) {
      toast.error('Failed to create case');
    }
  };

  // Bulk Operations
  const toggleSelectAll = () => {
    if (selectedCases.length === cases.length) {
      setSelectedCases([]);
    } else {
      setSelectedCases(cases.map(c => c.id));
    }
  };

  const toggleSelectCase = (caseId) => {
    setSelectedCases(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const handleBulkUpdate = async () => {
    if (selectedCases.length === 0) return;
    
    const updateData = { case_ids: selectedCases };
    if (bulkAction.status) updateData.status = bulkAction.status;
    if (bulkAction.priority) updateData.priority = bulkAction.priority;
    if (bulkAction.assigned_to) updateData.assigned_to = bulkAction.assigned_to;
    
    if (!updateData.status && !updateData.priority && !updateData.assigned_to) {
      toast.error('Please select at least one field to update');
      return;
    }
    
    setBulkLoading(true);
    try {
      const response = await caseAPI.bulkUpdate(updateData);
      toast.success(`Updated ${response.data.updated} cases`);
      setSelectedCases([]);
      setShowBulkModal(false);
      setBulkAction({ status: '', priority: '', assigned_to: '' });
      fetchCases();
    } catch (error) {
      toast.error('Failed to update cases');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCases.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCases.length} cases? This action cannot be undone.`)) {
      return;
    }
    
    setBulkLoading(true);
    try {
      const response = await caseAPI.bulkDelete(selectedCases);
      toast.success(`Deleted ${response.data.deleted} cases`);
      setSelectedCases([]);
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete cases');
    } finally {
      setBulkLoading(false);
    }
  };

  const getStatusConfig = (statusValue) => {
    return CASE_STATUSES.find(s => s.value === statusValue) || CASE_STATUSES[0];
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Case Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString()} investigation cases
          </p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-case-btn">
              <Plus className="w-4 h-4" />
              New Case
            </Button>
          </DialogTrigger>
          <DialogContent className="glass border-border">
            <DialogHeader>
              <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
                Create Investigation Case
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCase} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="label-mono">Case Title</Label>
                <Input
                  value={newCase.title}
                  onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                  placeholder="Enter case title"
                  className="bg-black/20 border-white/10"
                  required
                  data-testid="case-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Description</Label>
                <Textarea
                  value={newCase.description}
                  onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                  placeholder="Case description and initial findings..."
                  className="bg-black/20 border-white/10 min-h-[100px]"
                  required
                  data-testid="case-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Priority</Label>
                <Select
                  value={newCase.priority}
                  onValueChange={(value) => setNewCase({ ...newCase, priority: value })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="case-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-case-btn">
                  Create Case
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10" data-testid="filter-case-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Status</SelectItem>
                {CASE_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priority || "all"} onValueChange={(v) => setPriority(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10" data-testid="filter-case-priority">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Priorities</SelectItem>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              onClick={() => {
                setStatus('');
                setPriority('');
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Toolbar */}
      {selectedCases.length > 0 && (
        <Card className="glass border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">
                  {selectedCases.length} case{selectedCases.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="gap-2" data-testid="bulk-update-btn">
                      <Edit className="w-4 h-4" />
                      Bulk Update
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass border-border">
                    <DialogHeader>
                      <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
                        Bulk Update {selectedCases.length} Cases
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="label-mono">Change Status</Label>
                        <Select
                          value={bulkAction.status || "none"}
                          onValueChange={(v) => setBulkAction(prev => ({ ...prev, status: v === "none" ? "" : v }))}
                        >
                          <SelectTrigger className="bg-black/20 border-white/10">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            <SelectItem value="none">No change</SelectItem>
                            {CASE_STATUSES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="label-mono">Change Priority</Label>
                        <Select
                          value={bulkAction.priority || "none"}
                          onValueChange={(v) => setBulkAction(prev => ({ ...prev, priority: v === "none" ? "" : v }))}
                        >
                          <SelectTrigger className="bg-black/20 border-white/10">
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            <SelectItem value="none">No change</SelectItem>
                            {PRIORITIES.map((p) => (
                              <SelectItem key={p} value={p}>
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button variant="ghost" onClick={() => setShowBulkModal(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleBulkUpdate} disabled={bulkLoading} data-testid="confirm-bulk-update-btn">
                          {bulkLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                          Update Cases
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Button 
                  size="sm" 
                  variant="destructive" 
                  className="gap-2"
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected
                </Button>
                
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setSelectedCases([])}
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cases Table */}
      <Card className="glass">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading cases...</p>
            </div>
          ) : cases.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectedCases.length === cases.length && cases.length > 0}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Case #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>SAR</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cases.map((caseItem) => {
                  const statusConfig = getStatusConfig(caseItem.status);
                  const isSelected = selectedCases.includes(caseItem.id);
                  return (
                    <TableRow 
                      key={caseItem.id} 
                      className={`border-white/5 cursor-pointer hover:bg-white/5 ${isSelected ? 'bg-primary/5' : ''}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectCase(caseItem.id)}
                          aria-label={`Select case ${caseItem.case_number}`}
                          data-testid={`select-case-${caseItem.id}`}
                        />
                      </TableCell>
                      <TableCell onClick={() => onSelectCase(caseItem.id)}>
                        <span className="font-mono text-primary text-xs">
                          {caseItem.case_number}
                        </span>
                      </TableCell>
                      <TableCell onClick={() => onSelectCase(caseItem.id)}>
                        <div>
                          <p className="font-medium text-white">{caseItem.title}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {caseItem.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell onClick={() => onSelectCase(caseItem.id)}>
                        <Badge
                          variant="outline"
                          className={`${getRiskClass(caseItem.priority)} text-[10px] font-mono uppercase`}
                        >
                          {caseItem.priority}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={() => onSelectCase(caseItem.id)}>
                        <Badge
                          variant="outline"
                          className={`${statusConfig.color} text-[10px] font-mono uppercase`}
                        >
                          {caseItem.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={() => onSelectCase(caseItem.id)}>
                        {caseItem.sar_generated ? (
                          <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-400 border-green-400">
                            Generated
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs" onClick={() => onSelectCase(caseItem.id)}>
                        {formatRelativeTime(caseItem.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          data-testid={`view-case-${caseItem.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectCase(caseItem.id);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No cases found</p>
              <p className="text-sm text-muted-foreground/50 mt-1">
                Create a new case to start an investigation
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Case Detail Component
function CaseDetail({ caseId, onBack }) {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkedEntities, setLinkedEntities] = useState([]);
  const [linkedAlerts, setLinkedAlerts] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [generatingSAR, setGeneratingSAR] = useState(false);

  useEffect(() => {
    fetchCaseDetails();
  }, [caseId]);

  const fetchCaseDetails = async () => {
    setLoading(true);
    try {
      const response = await caseAPI.get(caseId);
      setCaseData(response.data);
      setEditForm({
        status: response.data.status,
        priority: response.data.priority,
        title: response.data.title,
        description: response.data.description,
      });

      // Fetch linked entities
      if (response.data.entity_ids?.length > 0) {
        const entitiesRes = await entityAPI.list({ limit: 100 });
        const linked = entitiesRes.data.items.filter(e => 
          response.data.entity_ids.includes(e.id)
        );
        setLinkedEntities(linked);
      }

      // Fetch linked alerts
      if (response.data.alert_ids?.length > 0) {
        const alertsRes = await alertAPI.list({ limit: 100 });
        const linked = alertsRes.data.items.filter(a => 
          response.data.alert_ids.includes(a.id)
        );
        setLinkedAlerts(linked);
      }
    } catch (error) {
      console.error('Failed to fetch case:', error);
      toast.error('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setAddingNote(true);
    try {
      await caseAPI.addNote(caseId, { content: newNote });
      toast.success('Note added');
      setNewNote('');
      fetchCaseDetails();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleUpdateCase = async () => {
    try {
      await caseAPI.update(caseId, editForm);
      toast.success('Case updated');
      setEditing(false);
      fetchCaseDetails();
    } catch (error) {
      toast.error('Failed to update case');
    }
  };

  const handleGenerateSAR = async () => {
    setGeneratingSAR(true);
    try {
      const response = await aiAPI.generateSAR(caseId);
      toast.success('SAR generated successfully');
      fetchCaseDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate SAR');
    } finally {
      setGeneratingSAR(false);
    }
  };

  const getStatusConfig = (statusValue) => {
    return CASE_STATUSES.find(s => s.value === statusValue) || CASE_STATUSES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Case not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Back to Cases
        </Button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(caseData.status);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="case-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack}
            data-testid="back-to-cases-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-primary text-sm">{caseData.case_number}</span>
              <Badge
                variant="outline"
                className={`${statusConfig.color} text-[10px] font-mono uppercase`}
              >
                {caseData.status.replace('_', ' ')}
              </Badge>
              <Badge
                variant="outline"
                className={`${getRiskClass(caseData.priority)} text-[10px] font-mono uppercase`}
              >
                {caseData.priority}
              </Badge>
            </div>
            <h1 className="font-secondary text-2xl font-bold text-white">
              {caseData.title}
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              {caseData.description}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {!editing ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => setEditing(true)}
                data-testid="edit-case-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button 
                onClick={handleGenerateSAR}
                disabled={generatingSAR || caseData.sar_generated}
                data-testid="generate-sar-btn"
              >
                {generatingSAR ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {caseData.sar_generated ? 'SAR Generated' : 'Generate SAR'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleUpdateCase}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <Card className="glass border-primary/30">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-mono">Status</Label>
                <Select 
                  value={editForm.status} 
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CASE_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Priority</Label>
                <Select 
                  value={editForm.priority} 
                  onValueChange={(v) => setEditForm({ ...editForm, priority: v })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Title</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="bg-black/20 border-white/10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-black/20 border-white/10"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="timeline" className="space-y-4">
        <TabsList className="glass">
          <TabsTrigger value="timeline" className="gap-2">
            <Clock className="w-4 h-4" />
            Timeline & Notes
          </TabsTrigger>
          <TabsTrigger value="entities" className="gap-2">
            <Users className="w-4 h-4" />
            Linked Entities ({linkedEntities.length})
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Linked Alerts ({linkedAlerts.length})
          </TabsTrigger>
        </TabsList>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Case Info */}
            <Card className="glass">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider">
                  Case Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <p className="label-mono">Created</p>
                  <p className="text-white">{formatDate(caseData.created_at)}</p>
                </div>
                <div>
                  <p className="label-mono">Last Updated</p>
                  <p className="text-white">{formatRelativeTime(caseData.updated_at)}</p>
                </div>
                <div>
                  <p className="label-mono">Assigned To</p>
                  <p className="text-white">{caseData.assigned_to || 'Unassigned'}</p>
                </div>
                {caseData.sar_generated && (
                  <div className="pt-3 border-t border-white/10">
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <FileText className="w-3 h-3 mr-1" />
                      SAR Report Available
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes Section */}
            <Card className="glass lg:col-span-2">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Investigation Notes ({caseData.notes?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {/* Add Note Form */}
                <form onSubmit={handleAddNote} className="mb-4">
                  <div className="flex gap-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add investigation notes, findings, or observations..."
                      className="bg-black/20 border-white/10 min-h-[80px]"
                      data-testid="note-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={addingNote || !newNote.trim()}
                      className="self-end"
                      data-testid="add-note-btn"
                    >
                      {addingNote ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </form>

                {/* Notes List */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {caseData.notes?.length > 0 ? (
                    [...caseData.notes].reverse().map((note, idx) => (
                      <div 
                        key={note.id || idx} 
                        className="p-3 rounded-lg bg-white/5 border border-white/5"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-white">
                            {note.author_name || note.author || 'Anonymous'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(note.created_at || note.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {note.content}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p>No notes yet</p>
                      <p className="text-xs mt-1 text-muted-foreground/50">
                        Add notes to document your investigation
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Entities Tab */}
        <TabsContent value="entities">
          <Card className="glass">
            <CardContent className="p-0">
              {linkedEntities.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="border-white/5">
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Risk Level</TableHead>
                      <TableHead>Country</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedEntities.map((entity) => (
                      <TableRow key={entity.id} className="border-white/5">
                        <TableCell className="font-medium text-white">
                          {entity.name}
                        </TableCell>
                        <TableCell className="capitalize text-muted-foreground">
                          {entity.entity_type}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`${getRiskClass(entity.risk_level)} text-[10px] font-mono uppercase`}
                          >
                            {entity.risk_level}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {entity.country || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No linked entities</p>
                  <p className="text-sm text-muted-foreground/50 mt-1">
                    Entities will be linked as the investigation progresses
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card className="glass">
            <CardContent className="p-0">
              {linkedAlerts.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="border-white/5">
                      <TableHead>Alert</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedAlerts.map((alert) => (
                      <TableRow key={alert.id} className="border-white/5">
                        <TableCell>
                          <p className="font-medium text-white">{alert.title}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {alert.description}
                          </p>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {alert.alert_type?.replace('_', ' ')}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`${getRiskClass(alert.severity)} text-[10px] font-mono uppercase`}
                          >
                            {alert.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="capitalize text-muted-foreground">
                          {alert.status}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {formatRelativeTime(alert.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No linked alerts</p>
                  <p className="text-sm text-muted-foreground/50 mt-1">
                    Alerts will be linked as they are escalated to this case
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Main Cases Component
export default function Cases() {
  const { id } = useParams();
  const navigate = useNavigate();

  const handleSelectCase = (caseId) => {
    navigate(`/cases/${caseId}`);
  };

  const handleBack = () => {
    navigate('/cases');
  };

  if (id) {
    return <CaseDetail caseId={id} onBack={handleBack} />;
  }

  return <CaseList onSelectCase={handleSelectCase} />;
}
